import { ReactNode, useState } from 'react';
import { UseSpringProps, animated, config, useSpring } from 'react-spring';
import styled from 'styled-components';

const RelativeWrapper = styled.span`
  position: relative;
  transform-style: preserve-3d;
  perspective: 200px;
`;
const AnimatedWrapper = styled(animated.span)`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const AbsoluteAnimatedWrapper = styled(AnimatedWrapper)`
  position: absolute;
  top: 0;
  left: 0;
`;

const initialPosition = {
  opacity: 1,
  transform: 'translateZ(0px) translateY(0px) rotateX(0deg) scale(1)',
};
const fadeAwayPosition = {
  opacity: 0,
  transform: 'translateZ(0px) translateY(0px) rotateX(0deg) scale(0.8)',
};
const entrancePosition = {
  opacity: 0,
  transform: 'translateZ(100px) translateY(10px) rotateX(-90deg) scale(1)',
};
const bouncePosition = {
  opacity: 1,
  transform: 'translateZ(60px) translateY(0px) rotateX(0deg) scale(1)',
};
const hiddenPosition = {
  opacity: 0,
  transform: 'translateZ(0px) translateY(0px) rotateX(0deg) scale(1)',
};

export type Props = {
  children: ReactNode;
  value: number;
  renderNumberIncrement?: (animationProps: UseSpringProps) => ReactNode;
};
export const FlippingIconIncrement = ({
  children,
  value,
  renderNumberIncrement,
}: Props) => {
  const [numberToAdd, setNumberToAdd] = useState(0);
  const [lastValue, setLastValue] = useState(value);

  if (value !== lastValue) {
    setLastValue(value);

    setNumberToAdd(value > lastValue ? value - lastValue : 0);
  }
  const totalDuration = 300 + numberToAdd * 100;
  const individualStepDuration = totalDuration / numberToAdd;
  const fadeAway = {
    ...fadeAwayPosition,
    reset: true,
    config: { duration: individualStepDuration },
  };
  const appear = {
    ...initialPosition,
    reset: true,
    config: { duration: individualStepDuration },
  };
  const bounce = [
    { ...bouncePosition, config: config.stiff },
    { ...initialPosition, config: config.stiff },
  ];
  const animation1 = useSpring({
    from: initialPosition,
    to: [...new Array(numberToAdd).fill(fadeAway), ...bounce],
    reset: true,
  });
  const animation2 = useSpring({
    from: entrancePosition,
    to: [...new Array(numberToAdd).fill(appear), hiddenPosition],
    reset: true,
  });

  return (
    <>
      <RelativeWrapper>
        <AnimatedWrapper style={numberToAdd > 0 ? animation1 : undefined}>
          {children}
        </AnimatedWrapper>
        <AbsoluteAnimatedWrapper
          style={numberToAdd > 0 ? animation2 : undefined}
        >
          {children}
        </AbsoluteAnimatedWrapper>
      </RelativeWrapper>
      {renderNumberIncrement?.({
        config: {
          duration: totalDuration,
        },
      })}
    </>
  );
};
